import React, { useEffect, useReducer, useContext } from "react";
import ParticipationOverview from "./ParticipationOverview";
import { sendRequest } from "../../common/http";
import "./Sll.scss";
import AssessmentProgressChart from "./AssessmentProgressChart";
import translate from "@/i18n/translate";
import WidgetLoader from "../common/elements/WidgetLoader";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import ApiError from "../common/elements/ApiError";
import { ReportingYear } from "../common/interfaces/Interface";
import { WmButton } from "@watermarkinsights/ripple-react";

export interface ParticipationOverviewInterface {
  assessmentCount: number;
  orgCount: number;
  studentCount: number;
}

interface StudentAssessments {
  awaitingAssessment: number;
  awaitingSubmission: number;
  done: number;
}

export interface StudentAssessmentProgressByTerm {
  name: String;
  studentAssessments: StudentAssessments;
  uuid: String;
}

export interface LearningAssessments {
  noActivityTerms: string;
  participationOverview: ParticipationOverviewInterface | null;
  studentAssessmentProgressByTerm: StudentAssessmentProgressByTerm[];
}

interface SLLState {
  hideWidget: boolean;
  loader: boolean;
  apiError: boolean;
  learningAssessments: LearningAssessments;
}

interface SLLAction {
  type: string;
  apiError: boolean;
  learningAssessments: LearningAssessments;
}

const defaultSLLAction = {
  type: "",
  apiError: false,
  learningAssessments: {
    noActivityTerms: "",
    participationOverview: null,
    studentAssessmentProgressByTerm: []
  }
};

const stateReducer = (state: SLLState, action: SLLAction) => {
  switch (action.type) {
    case "showLoader":
      return { ...state, loader: true };
    case "setLearningAssessments":
      return {
        ...state,
        hideWidget: action.learningAssessments.participationOverview === null,
        learningAssessments: action.learningAssessments,
        loader: false,
        apiError: false
      };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};

export interface SllProps {
  selectedReportingYear: ReportingYear;
  onComponentLoad: (component: string) => void;
}
export default function Sll({
  selectedReportingYear,
  onComponentLoad
}: SllProps) {
  const { institution } = useContext<AppContextInterface>(AppContext);

  const initialState = {
    hideWidget: institution && institution.platformUuid === null ? true : false,
    loader: true,
    apiError: false,
    learningAssessments: {
      noActivityTerms: "",
      participationOverview: null,
      studentAssessmentProgressByTerm: []
    }
  };

  const [state, dispatch] = useReducer(stateReducer, initialState);

  useEffect(() => {
    if (institution && institution.platformUuid && selectedReportingYear.uuid) {
      dispatch({ ...defaultSLLAction, type: "setApiError", apiError: false });

      const applyResult = (result: LearningAssessments) => {
        dispatch({
          ...defaultSLLAction,
          type: "setLearningAssessments",
          learningAssessments: result
        });
        onComponentLoad("sll");
      };

      const onError = () => {
        dispatch({ ...defaultSLLAction, type: "setApiError", apiError: true });
        onComponentLoad("sll");
      };

      dispatch({ ...defaultSLLAction, type: "showLoader" });

      sendRequest(
        {
          uri: `/sll/learning_assessments?start_date=${
            selectedReportingYear.startDate
          }&end_date=${selectedReportingYear.endDate}&with_activities=${false}`,
          camelize: true
        },
        applyResult,

        onError
      );
    }
  }, [selectedReportingYear, institution && institution.platformUuid]);

  const openSllHTMLViewLink = () => {
    return window.open(getURL(), "_blank");
  };

  const getURL = () => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    const isPlanning = window.location.pathname.includes("/planning/");
    const base = isPlanning ? `/insights/planning` : `/insights`;
    let url = `${base}/institution/${institution?.platformUuid ?? ""}/sll/reporting_year/${selectedReportingYear.uuid}/html`;
    if (locale !== null) {
      url = `${url}?locale=${locale}`;
    }

    return url;
  };

  const header = () => {
    return (
      <div className="header">
        <div className="info">
          <h3 className="title">{translate("app.sll.learningAssessment")}</h3>
          <p className="description">{translate("app.sll.reviewProgress")}</p>
        </div>
        {state.learningAssessments.studentAssessmentProgressByTerm?.length >
          0 &&
        !state.loader &&
        !state.apiError ? (
          <div>
            <WmButton
              id="export-button"
              tabIndex={0}
              onClick={openSllHTMLViewLink}
              button-type="secondary"
            >
              {translate("app.exportHtml")}
            </WmButton>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  var body = () => {
    return (
      <>
        {header()}
        <div className="body">
          {state.loader && !state.apiError && (
            <WidgetLoader
              loaderContainerClasses="loader-container"
              loaderClasses="widget-loader"
            />
          )}
          {!state.loader && !state.apiError && (
            <>
              {state.learningAssessments.studentAssessmentProgressByTerm
                ?.length > 0 && (
                <div id="participation-overview" className="sll-section">
                  <ParticipationOverview
                    overview={state.learningAssessments.participationOverview}
                    reportingYear={selectedReportingYear.name}
                  />
                  <div className="middle-section"></div>
                  <AssessmentProgressChart
                    assessmentProgress={
                      state.learningAssessments.studentAssessmentProgressByTerm
                    }
                    noActivityTerms={state.learningAssessments.noActivityTerms}
                  ></AssessmentProgressChart>
                </div>
              )}
              {state.learningAssessments.studentAssessmentProgressByTerm
                ?.length === 0 && <NoLearningAssessments />}
            </>
          )}
          {state.apiError && <ApiError />}
        </div>
      </>
    );
  };

  return state.hideWidget ? (
    <React.Fragment />
  ) : (
    <div className="grid-container wm-grid-col-12 wm-grid-col-md-10">
      <div className="paper-card paper-card-height">{body()}</div>
    </div>
  );
}
const NoLearningAssessments = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text">{translate("app.sll.noAssessments")}</div>
      <div className="no-plans-text-select">
        {translate("app.sll.noAssessmentsSubText")}
      </div>
    </div>
  );
};
